import { Grid } from "components/grid";
import TinyBadge from "components/tiny-badge";
import { uref } from "@/utils/reactive/uref";
import {
  type EAssetType,
  type IAsset,
  type IAttachment,
  type TDocRef,
} from "findef";
import { computed, defineComponent, type MaybeRef } from "vue";
import { AssetImageV2 } from "components/asset-image-v2";
import { useResponsive } from "~/composables/useResponsive";



export const TickerOption = defineComponent<{
  image?: TDocRef<IAttachment>;
  assetType?: EAssetType;
  listed?: boolean;
  symbol?: string;
  name: string;
  badge?: string;
  isOpen?: MaybeRef<boolean>;
  noImage?: boolean
}>(
  (props) => {
    const { isSmall } = useResponsive();
    const isMobile = computed(() => isSmall.value);
    const isOpen = computed(() => !!uref(props.isOpen));
    const name = computed(() => props.name);
    const symbol = computed(() =>
      props.symbol && props.symbol.length > 0 && props.symbol !== ""
        ? props.symbol
        : "No identifier",
    );

    const partialAsset = computed<Partial<IAsset>>(() => ({
      type: props.assetType,
      listed: !!props.listed,
      symbol: props.noImage ? undefined : props.symbol,
      image: props.noImage ? undefined : props.image,
    }));

    return () => {
      return (
        <Grid
          class="w-full h-full min-w-0"
          columns="minmax(0, 1fr) max-content"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid
            columns="max-content 1fr"
            alignItems="center"
            gap="0.625rem"
            class="min-w-0"
          >
            <div
              class={[
                "h-full justify-center items-center flex",
                "bg-gray-100",
                isOpen.value ? "w-10" : "w-6",
              ]}
              style={{ aspectRatio: "1 / 1" }}
            >
              <div
                class={[
                  "justify-center relative items-center flex",
                  isOpen.value ? "w-8 h-8 p-[1.92px]" : "w-4 h-4",
                  "p-0 w-full h-full",
                ]}
              >
                <AssetImageV2
                  asset={partialAsset.value}
                  size="full"
                  variant="gray"
                  iconProps={{ size: "70%" }}
                />
              </div>
            </div>
            <div class="min-w-0 max-w-full overflow-hidden">
              {isOpen.value ? (
                <div class="flex-col justify-start items-start flex min-w-0 max-w-full">
                  <div class="text-[#111928] text-xs font-semibold font-['Inter'] truncate w-full">
                    {name.value}
                  </div>
                  <div class="text-gray-500 text-xs font-normal font-['Inter'] wrap-break-word">
                    {symbol.value}
                  </div>
                </div>
              ) : (
                <div class="text-[#111928] text-sm font-normal font-['Inter'] flex items-center min-w-0">
                  <span class="truncate">{name.value}</span>
                </div>
              )}
            </div>
          </Grid>
          {isOpen.value ? (
            props.badge && !isMobile.value && (
                <TinyBadge
                  style={{
                    height: "22px",
                    paddingTop: "0px",
                    paddingBottom: "0px",
                  }}
                  variant="gray"
                  type="mltslct"
                  textClass="text-xs font-semibold"
                >
                    {props.badge}
                </TinyBadge>
            )
          ) : (
            <div>
              <span class="text-gray-500 text-sm font-normal font-['Inter']">
                {symbol.value}
              </span>
            </div>
          )}
        </Grid>
      );
    };
  },
  {
    props: [
      "symbol",
      "badge",
      "name",
      "image",
      "isOpen",
      "assetType",
      "listed",
      "noImage"
    ],
  },
);
